import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

interface IReactPopover {
  children: React.ReactNode;
  content: React.ReactNode;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  trigger?: string;
  open?: boolean;
  size?: number;
  onClose: () => void;
  contentClass?: string;
}

const ReactPopover: React.FC<IReactPopover> = ({
  children,
  content,
  header,
  footer,
  contentClass,
  trigger = 'click',
  size = 8,
  open = false,
  onClose,
}) => {
  const [show, setShow] = useState(open);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [mounted, setMounted] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, right: 0 });

  // Calculate popover position based on trigger element
  const updatePosition = useCallback(() => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      setPopoverPosition({
        top: rect.bottom,
        right: window.innerWidth - rect.right,
      });
    }
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Update position when show changes
  useEffect(() => {
    if (show) {
      updatePosition();
    }
  }, [show, updatePosition]);

  const handleMouseOver = () => {
    if (trigger === 'hover') {
      setShow(true);
    }
  };

  const handleMouseLeft = () => {
    if (trigger === 'hover') {
      setShow(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideWrapper = wrapperRef.current?.contains(target);
      const isInsideContent = contentRef.current?.contains(target);

      if (!isInsideWrapper && !isInsideContent) {
        setShow(false);
        onClose();
      }
    };

    if (show) {
      // Bind the event listener
      document.addEventListener('mousedown', handleClickOutside);
      // Disable body scroll
      document.body.style.overflow = 'hidden';
    } else {
      // Enable body scroll when not showing
      document.body.style.overflow = 'auto';
    }

    return () => {
      // Unbind the event listener and enable body scroll on clean up
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'auto';
    };
  }, [show, onClose]);

  useEffect(() => {
    // Set show state based on open prop
    setShow(open);
  }, [open]);

  const handleClose = () => {
    setShow(false);
    onClose();
  };

  // Render backdrop and content via portal to document.body for proper z-index stacking
  const renderPopoverContent = () => {
    if (!show || !mounted) return null;

    return createPortal(
      <>
        {/* backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-50 transition-opacity z-[99998]"
          onClick={handleClose}
        />
        {/* popover content - Desktop: fixed position below trigger, Mobile: bottom sheet */}
        <div
          ref={contentRef}
          className={`fixed z-[99999] ${contentClass}`}
          style={{
            // Desktop: position below trigger element
            top: window.innerWidth >= 768 ? `${popoverPosition.top + 10}px` : 'auto',
            right: window.innerWidth >= 768 ? `${popoverPosition.right}px` : '0px',
            left: window.innerWidth >= 768 ? 'auto' : '0px',
            bottom: window.innerWidth >= 768 ? 'auto' : '0px',
          }}
        >
          <div className="md:rounded-xl md:min-w-[200px] md:max-h-[70vh] rounded-t-3xl bg-white p-4 shadow-[0px_4px_20px_rgba(0,0,0,0.12)]">
            <div className="sticky w-full">{header || null}</div>
            <div className="overflow-auto max-h-[70vh] no-scrollbar bg-fixed">{content}</div>
            <div>{footer || null}</div>
          </div>
        </div>
      </>,
      document.body,
    );
  };

  return (
    <div
      ref={wrapperRef}
      onMouseEnter={handleMouseOver}
      onMouseLeave={handleMouseLeft}
      className="w-full md:relative flex justify-center px-2"
    >
      <div className={'cursor-pointer'} onClick={() => setShow(!show)}>
        {children}
      </div>
      {renderPopoverContent()}
    </div>
  );
};

export default ReactPopover;
